<template>
    <div class="panel-contents">
        <div class="d-flex align-items-center">
            <h3 v-if="thread">{{ thread.predicted_subject }}</h3>
            <div v-else class="d-flex">
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary"
                    role="status"
                ></div>
            </div>
        </div>

        <div class="thread-messages-container" ref="messagesContainer">
            <template v-if="thread">
                <template
                    v-for="transaction in thread.responses"
                    :key="transaction.id"
                >
                    <!-- User Message -->
                    <MessageBubble
                        :user="user"
                        align="right"
                        :created-at="transaction.created_at"
                    >
                        <template #content>
                            <CitationText
                                :message-text="transaction.input_text"
                            />
                        </template>
                    </MessageBubble>

                    <!-- Guardian Message -->
                    <div
                        v-if="transaction.output_text"
                        class="d-flex flex-column"
                        style="gap: var(--space-2)"
                    >
                        <MessageBubble
                            align="left"
                            :static-name="guardianName"
                            :static-image="guardianImage"
                            :created-at="transaction.created_at"
                        >
                            <template #content>
                                <CitationText
                                    :message-text="transaction.output_text"
                                    :citations="transaction.annotations"
                                    @open-pdf="openPdf"
                                />
                            </template>
                        </MessageBubble>

                        <!-- Guardian Response Message Actions -->
                        <div
                            class="d-flex flex-row align-items-center justify-content-end"
                            style="gap: var(--space-3); min-height: 24px"
                        >
                            <button
                                class="action-btn"
                                @click="handleRate(transaction.id, 'GOOD')"
                                :class="{
                                    selected: transaction.rating === 'GOOD',
                                }"
                            >
                                <img
                                    v-if="transaction.rating === 'GOOD'"
                                    :src="thumbsUpFilled"
                                />
                                <i v-else class="fi fi-thumbs-up"></i>
                            </button>

                            <button
                                class="action-btn"
                                @click="handleRate(transaction.id, 'BAD')"
                                :class="{
                                    selected: transaction.rating === 'BAD',
                                }"
                            >
                                <img
                                    v-if="transaction.rating === 'BAD'"
                                    :src="thumbsDownFilled"
                                />
                                <i v-else class="fi fi-thumbs-down"></i>
                            </button>

                            <button
                                class="action-btn"
                                @click="
                                    handleCopy(
                                        transaction.output_text,
                                        transaction.id
                                    )
                                "
                            >
                                <i
                                    v-if="copyingIds[transaction.id]"
                                    class="fi fi-check check-icon"
                                ></i>
                                <i v-else class="fi fi-copy"></i>
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Suggestion Text -->
                <div
                    v-if="showSuggestion"
                    style="
                        font-size: 0.875rem;
                        font-style: italic;
                        color: var(--neutral-700);
                    "
                >
                    {{ $t('guardians.thread.suggestion') }}
                    <a
                        href="#"
                        class="text-link"
                        @click.prevent="openSuggestedGuardian"
                    >
                        {{ suggestedGuardianLinkText }}
                    </a>
                    {{ $t('guardians.thread.suggestionSuffix') }}
                </div>
            </template>

            <!-- LIVE Question - User Message which is currently being processed -->
            <template v-if="fetching">
                <MessageBubble
                    :user="user"
                    align="right"
                    :created-at="new Date().toISOString()"
                >
                    <template #content>
                        <CitationText :message-text="guardianStore.queryText" />
                    </template>
                </MessageBubble>

                <transition>
                    <MessageBubble
                        v-if="textStream"
                        align="left"
                        :static-name="guardianName"
                        :static-image="guardianImage"
                        :created-at="new Date().toISOString()"
                    >
                        <template #content>
                            <CitationText :message-text="textStream" />
                        </template>
                    </MessageBubble>
                </transition>
            </template>
        </div>

        <MessageField
            @submit="submitMessageToThread()"
            v-model="messageInput"
            :fetching="fetching"
        ></MessageField>

        <!-- PDF Viewer -->
        <PdfViewerSidekick
            :visible="pdfVisible"
            :current-pdf-file="currentPdfFile"
            @close-pdf="closePdf"
        />
    </div>
</template>

<script>
import MessageBubble from '@/components/patty/MessageBubble.vue';
import PdfViewerSidekick from '@/components/pdf_viewer/PdfViewerSidekick.vue';
import CitationText from '@/components/patty/CitationText.vue';
import MessageField from '@/components/fields/MessageField.vue';
import { useGuardianStore } from '@/stores/guardian';
import { findSuggestedGuardian } from '@/config/guardians.js';
import { getGuardianLabel } from '@/components/guardians/utils/guardianDisplay';

import thumbsUpFilled from '@/assets/images/icons/thumbs-up-filled.svg';
import thumbsDownFilled from '@/assets/images/icons/thumbs-down-filled.svg';

export default {
    name: 'GuardianThread',
    components: {
        MessageBubble,
        PdfViewerSidekick,
        CitationText,
        MessageField,
    },
    props: {
        threadId: {
            type: Number,
            required: true,
        },
        guardian: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            pdfVisible: false,
            currentPdfFile: null,
            copyingIds: {},

            fetching: false,
            thread: null,
            guardianStore: null,

            textStream: '',
            messageInput: '',

            thumbsUpFilled,
            thumbsDownFilled,

            suggestedGuardian: null,
            lastQueryText: '',
        };
    },

    watch: {
        threadId(newThreadId) {
            if (newThreadId > 0) {
                this.getThread();
            } else if (newThreadId === -1) {
                this.createThread();
            }
        },
    },

    computed: {
        user() {
            return this.$userStore.user;
        },
        guardianName() {
            return (
                this.guardian?.name ||
                this.$t('guardians.newChat.fallbacks.guardianName')
            );
        },
        guardianImage() {
            // Use imagePath if available (for UserAvatar component)
            return this.guardian?.imagePath || 'patty/patty-avatar.svg';
        },
        showSuggestion() {
            return (
                this.thread &&
                this.thread.responses?.length > 0 &&
                this.suggestedGuardian &&
                !this.fetching
            );
        },
        suggestedGuardianLabel() {
            return getGuardianLabel(
                this.suggestedGuardian,
                this.$t,
                this.$userStore?.company?.country,
                this.$userStore?.effectiveLocale
            );
        },
        suggestedGuardianLinkText() {
            return this.$t('guardians.thread.suggestedGuardianLink', {
                name: this.suggestedGuardian?.name || '',
                label: this.suggestedGuardianLabel,
            });
        },
    },

    methods: {
        openPdf(fileHash) {
            this.pdfVisible = true;
            this.currentPdfFile = fileHash;
        },
        closePdf() {
            this.pdfVisible = false;
            this.currentPdfFile = null;
        },
        handleRate(transactionId, status) {
            const payload = {
                id: transactionId,
                rating: status,
            };

            this.$postData(
                `openai/assistant-thread/message/rate`,
                payload
            ).then((response) => {
                if (response.success) {
                    this.thread.responses.forEach((transaction) => {
                        if (transaction.id === transactionId)
                            transaction.rating = status;
                    });
                } else {
                    this.$alertStore.pushError(
                        this.$t('guardians.alerts.rateMessageError')
                    );
                }
            });
        },
        handleCopy(message, messageId) {
            navigator.clipboard.writeText(message);
            this.copyingIds[messageId] = true;
            setTimeout(() => {
                this.copyingIds[messageId] = false;
            }, 2000);
        },
        scrollToBottom(keepOnly = false) {
            if (this.$refs.messagesContainer) {
                const container = this.$refs.messagesContainer;

                if (keepOnly) {
                    const atBottom =
                        container.scrollTop + container.clientHeight >=
                        container.scrollHeight;
                    if (atBottom) {
                        this.$nextTick(() => {
                            container.scrollTop = container.scrollHeight;
                        });
                    }
                } else {
                    container.scrollTop = container.scrollHeight;
                }
            }
        },

        checkForNoRelevantInfo() {
            if (
                !this.thread ||
                !this.thread.responses ||
                this.thread.responses.length === 0
            )
                return;

            const lastResponse =
                this.thread.responses[this.thread.responses.length - 1];
            const outputText = lastResponse.output_text || '';

            if (outputText.includes('No relevant information found')) {
                const queryText = lastResponse.input_text || this.lastQueryText;
                this.suggestedGuardian = findSuggestedGuardian(
                    queryText,
                    this.guardian.agentType
                );
            } else {
                this.suggestedGuardian = null;
            }
        },

        openSuggestedGuardian() {
            if (!this.suggestedGuardian || !this.lastQueryText) return;

            const queryToReask = this.lastQueryText;
            this.guardianStore.closePanel();

            setTimeout(() => {
                // Pass the query text as first parameter (as a string, not -1)
                this.guardianStore.openThread(
                    queryToReask,
                    this.suggestedGuardian
                );
            }, 300);
        },

        submitMessageToThread() {
            if (this.fetching) return;

            this.textStream = '';
            this.fetching = true;

            const payload = {
                thread_id: this.thread.id,
                message: this.messageInput,
                agent_type: this.guardian.agentType,
            };

            this.lastQueryText = this.messageInput;
            this.guardianStore.setQueryText(this.messageInput);
            this.messageInput = '';

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/message/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-response') {
                        try {
                            const response = JSON.parse(chunk.data);
                            this.thread.responses.push(response);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('guardians.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;
                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.checkForNoRelevantInfo();
                    this.scrollToBottom(true);
                }
            });
        },

        createThread() {
            this.textStream = '';
            this.fetching = true;

            const payload = {
                message: this.guardianStore.queryText,
                agent_type: this.guardian.agentType,
            };

            this.lastQueryText = this.guardianStore.queryText;

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-thread') {
                        try {
                            this.thread = JSON.parse(chunk.data);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('guardians.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;

                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.guardianStore.setQueryText('');
                    this.checkForNoRelevantInfo();
                    this.scrollToBottom(true);
                }
            });
        },

        getThread() {
            this.fetching = true;

            const payload = {
                where: { id: this.threadId },
                include: { responses: true },
            };

            this.$getData('openai/assistant-thread/read', payload).then(
                (response) => {
                    if (response.success) {
                        this.thread = response.data.length
                            ? response.data[0]
                            : null;
                    } else if ('message' in response) {
                        this.$alertStore.pushError(response.message);
                    }

                    this.fetching = false;

                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            );
        },
    },

    created() {
        this.guardianStore = useGuardianStore();

        // Initialize thread based on threadId prop
        if (this.threadId > 0) {
            this.getThread();
        } else if (this.threadId === -1) {
            this.createThread();
        }
    },
};
</script>

<style scoped>
/* Fix guardian avatar cropping - show head/top part */
:deep(.avatar-image) {
    object-fit: cover;
    object-position: top center;
}

.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

.panel-contents {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-6);
    height: 100%;
    width: 100%;
}

.thread-messages-container {
    max-height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.thread-messages-container::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.thread-messages-container:hover::-webkit-scrollbar {
    opacity: 1;
}

.thread-messages-container::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

.separator {
    border-top: 1px solid var(--neutral-600);
}

.input-button {
    bottom: var(--space-2);
    right: var(--space-2);
}

.action-btn {
    background-color: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    height: 28px;
    width: 28px;
    position: relative;
    border-radius: 20%;
}

.action-btn img,
.action-btn i {
    transition: all 0.2s ease;
    width: var(--space-4);
    height: var(--space-4);
}

.action-btn:focus {
    outline: none;
}

.action-btn:hover {
    background-color: var(--primary-100);
    border: none;
    color: var(--neutral-900);
}

.selected {
    cursor: default;
    pointer-events: none;
}

.check-icon {
    animation: checkAppear 0.2s ease-in-out forwards;
}

@keyframes checkAppear {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    70% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.suggestion-banner {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4);
    background-color: var(--primary-50);
    border: 1px solid var(--primary-200);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    line-height: 1.5;
}

.suggestion-banner i {
    color: var(--primary-600);
    font-size: 1.25rem;
    flex-shrink: 0;
}

.suggestion-banner a {
    color: var(--primary-700);
    font-weight: 600;
    text-decoration: none;
}

.suggestion-banner a:hover {
    text-decoration: underline;
}
</style>
